import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    ChevronRight,
    GraduationCap,
    Loader2,
    Megaphone,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePortal } from "../PortalContext";

export default function HomeTab() {
    const navigate = useNavigate();
    const { applications, applicationsLoading: loading, announces = [], announcesLoading, handleComplete: onComplete, startApplication, pendingProgramSelection, startApplicationWithSelection } = usePortal();
    
    // Auto-start application if there's a pending program selection from homepage
    useEffect(() => {
        if (pendingProgramSelection) {
            startApplicationWithSelection(pendingProgramSelection);
        }
    }, [pendingProgramSelection, startApplicationWithSelection]);
    
    const onView = (app: { Application_Number: string }) => navigate(`/portal/applications/${app.Application_Number}`);
    const setActiveTab = (tab: string) => {
        const map: Record<string, string> = { Home: "/portal/home", Tasks: "/portal/tasks", Applications: "/portal/applications", "My Account": "/portal/account" };
        if (map[tab]) navigate(map[tab]);
    };
    const [currentIndex, setCurrentIndex] = useState(0);
    const current = announces[currentIndex];
    const prevSlide = () => setCurrentIndex((i) => (i - 1 + announces.length) % announces.length);
    const nextSlide = () => setCurrentIndex((i) => (i + 1) % announces.length);

    return (
        <>
            {/* Mobile layout: stacked */}
            {/* Announcement Carousel – mobile only */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="md:hidden w-full overflow-hidden rounded-3xl shadow-sm bg-white border border-slate-100"
            >
                {announcesLoading ? (
                    <div className="w-full h-[260px] flex items-center justify-center bg-slate-50">
                        <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />
                    </div>
                ) : announces.length === 0 ? (
                    <div className="w-full h-[260px] flex flex-col items-center justify-center bg-slate-50 gap-3">
                        <Megaphone className="h-10 w-10 text-slate-300" />
                        <p className="text-sm text-slate-400">There are no announcements to show</p>
                    </div>
                ) : (
                    <>
                        <div
                            className={`relative w-full h-[260px] bg-slate-50${current.target_url ? " cursor-pointer" : ""}`}
                            onClick={() => current.target_url && window.open(current.target_url, "_blank")}
                        >
                            {current.image_url && (
                                <img
                                    src={current.image_url}
                                    alt={current.header || ""}
                                    className="w-full h-full object-contain object-center select-none"
                                    draggable={false}
                                />
                            )}
                            {announces.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                                        className="absolute top-1/2 left-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-r text-xl z-10 transition-colors"
                                        aria-label="Previous"
                                    >
                                        &#10094;
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                                        className="absolute top-1/2 right-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-l text-xl z-10 transition-colors"
                                        aria-label="Next"
                                    >
                                        &#10095;
                                    </button>
                                    <span className="absolute bottom-2.5 left-1/2 -translate-x-1/2 text-white text-xs bg-black/40 px-3 py-0.5 rounded-full z-10 whitespace-nowrap pointer-events-none">
                                        {currentIndex + 1} / {announces.length}
                                    </span>
                                </>
                            )}
                        </div>
                        {(current.header || current.description) && (
                            <div className="px-4 py-3 text-left">
                                {current.header && (
                                    <h3 className="text-base font-semibold text-slate-800 mb-1">{current.header}</h3>
                                )}
                                {current.description && (
                                    <p className="text-sm text-slate-500">{current.description}</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </motion.div>

            {/* Mobile: recent applications card list */}
            <div className="md:hidden space-y-4">
                <div className="flex items-center justify-between px-2">
                    <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">Recent Applications</h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("Applications")} className="text-primary font-bold hover:bg-primary/5">
                        View All <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                </div>
                {loading ? (
                    <div className="flex flex-col gap-3">
                        {["recent-app-card-1", "recent-app-card-2"].map((key) => (
                            <Card key={key} className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 h-28 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {applications.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">You don't have applications to view.</p>
                        ) : (
                            applications.slice(0, 2).map((app) => (
                                <Card key={app.Application_Number} className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                                <GraduationCap className="h-4 w-4 text-slate-400" />
                                            </div>
                                            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                                                {app.Application_Number}
                                            </span>
                                        </div>
                                        <Badge
                                            variant="outline"
                                            className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap shrink-0 ${
                                                app.Action === "Complete"
                                                    ? "border-amber-100 text-amber-600 bg-amber-50/50"
                                                    : app.Stage === "Evaluation"
                                                      ? "border-blue-100 text-blue-600 bg-blue-50/50"
                                                      : "border-emerald-100 text-emerald-600 bg-emerald-50/50"
                                            }`}
                                        >
                                            {app.Stage || "—"}
                                        </Badge>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm font-semibold text-slate-800">{app.Applicant_Name}</p>
                                        <p className="text-xs text-slate-500">{app.Applied_Program_Name}</p>
                                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                            <span>{app.Stage}</span>
                                            {app.Applied_Term && <><span>·</span><span>{app.Applied_Term}</span></>}
                                        </div>
                                    </div>
                                    <Button
                                        variant={app.Action === "Complete" ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => app.Action === "Complete" ? onComplete(app) : onView(app)}
                                        className={`w-full h-9 font-bold text-xs rounded-xl ${
                                            app.Action === "Complete"
                                                ? "bg-primary hover:bg-[#002b66]"
                                                : "border-slate-200 text-slate-600 hover:text-primary"
                                        }`}
                                    >
                                        {app.Action === "Complete" ? "Complete Application" : "View Details"}
                                    </Button>
                                </Card>
                            ))
                        )}
                        <Button
                            onClick={() => startApplication()}
                            className="w-full h-10 font-bold text-xs rounded-xl bg-primary hover:bg-[#002b66] text-white"
                        >
                            Apply Now
                        </Button>
                    </div>
                )}
            </div>

            {/* Desktop layout: 2/3 announcements + 1/3 recent applications side by side */}
            <div className="hidden md:flex gap-4 items-start">
                {/* Left: Announcement Carousel (2/3) */}
                <div className="flex-[2] min-w-0 space-y-4">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full overflow-hidden rounded-3xl shadow-sm bg-white border border-slate-100"
                    >
                        {announcesLoading ? (
                            <div className="w-full h-[420px] flex items-center justify-center bg-slate-50">
                                <Loader2 className="h-10 w-10 text-slate-300 animate-spin" />
                            </div>
                        ) : announces.length === 0 ? (
                            <div className="w-full h-[420px] flex flex-col items-center justify-center bg-slate-50 gap-3">
                                <Megaphone className="h-12 w-12 text-slate-300" />
                                <p className="text-sm text-slate-400">There are no announcements to show</p>
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`relative w-full h-[420px] bg-slate-50${current.target_url ? " cursor-pointer" : ""}`}
                                    onClick={() => current.target_url && window.open(current.target_url, "_blank")}
                                >
                                    {current.image_url && (
                                        <img
                                            src={current.image_url}
                                            alt={current.header || ""}
                                            className="w-full h-full object-contain object-center select-none"
                                            draggable={false}
                                        />
                                    )}
                                    {announces.length > 1 && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                                                className="absolute top-1/2 left-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-r text-xl z-10 transition-colors"
                                                aria-label="Previous"
                                            >
                                                &#10094;
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                                                className="absolute top-1/2 right-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-l text-xl z-10 transition-colors"
                                                aria-label="Next"
                                            >
                                                &#10095;
                                            </button>
                                            <span className="absolute bottom-2.5 left-1/2 -translate-x-1/2 text-white text-xs bg-black/40 px-3 py-0.5 rounded-full z-10 whitespace-nowrap pointer-events-none">
                                                {currentIndex + 1} / {announces.length}
                                            </span>
                                        </>
                                    )}
                                </div>
                                {(current.header || current.description) && (
                                    <div className="px-4 py-3 text-left">
                                        {current.header && (
                                            <h3 className="text-base font-semibold text-slate-800 mb-1">{current.header}</h3>
                                        )}
                                        {current.description && (
                                            <p className="text-sm text-slate-500">{current.description}</p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                </div>

                {/* Right: Recent Applications (1/3) */}
                <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex items-center justify-between px-1">
                        <h3 className="text-lg font-serif font-black text-slate-900 tracking-tight">Recent Applications</h3>
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab("Applications")} className="text-primary font-bold hover:bg-primary/5">
                            View All <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                    {loading ? (
                        <div className="space-y-3">
                            {["application-row-1", "application-row-2"].map((key) => (
                                <div key={key} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {applications.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-4">You don't have applications to view.</p>
                            ) : (
                                applications.slice(0, 5).map((app) => (
                                    <Card key={app.Application_Number} className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                                    <GraduationCap className="h-3.5 w-3.5 text-slate-400" />
                                                </div>
                                                <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                                                    {app.Application_Number}
                                                </span>
                                            </div>
                                            <Badge
                                                variant="outline"
                                                className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap shrink-0 ${
                                                    app.Action === "Complete"
                                                        ? "border-amber-100 text-amber-600 bg-amber-50/50"
                                                        : app.Stage === "Evaluation"
                                                          ? "border-blue-100 text-blue-600 bg-blue-50/50"
                                                          : "border-emerald-100 text-emerald-600 bg-emerald-50/50"
                                                }`}
                                            >
                                                {app.Stage || "—"}
                                            </Badge>
                                        </div>
                                        <div className="space-y-0.5">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{app.Applicant_Name}</p>
                                            <p className="text-xs text-slate-500 truncate">{app.Applied_Program_Name}</p>
                                        </div>
                                        <Button
                                            variant={app.Action === "Complete" ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => app.Action === "Complete" ? onComplete(app) : onView(app)}
                                            className={`w-full h-8 font-bold text-xs rounded-xl ${
                                                app.Action === "Complete"
                                                    ? "bg-primary hover:bg-[#002b66]"
                                                    : "border-slate-200 text-slate-600 hover:text-primary"
                                            }`}
                                        >
                                            {app.Action === "Complete" ? "Complete Application" : "View Details"}
                                        </Button>
                                    </Card>
                                ))
                            )}
                            <Button
                                onClick={() => startApplication()}
                                className="w-full h-10 font-bold text-xs rounded-xl bg-primary hover:bg-[#002b66] text-white"
                            >
                                Apply Now
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
